import { PDFDocument, degrees } from "pdf-lib";
import { drawAnnotation } from "./annotations";

const EXPORT_SCALE = 2;

const canvasToPngBytes = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(async (blob) => {
      if (!blob) {
        reject(new Error("Could not render annotations."));
        return;
      }
      resolve(new Uint8Array(await blob.arrayBuffer()));
    }, "image/png");
  });

function overlayPlacement(crop, rotation, width, height) {
  const size = { width, height };
  if (rotation === 90)
    return { ...size, x: crop.x + crop.width, y: crop.y, rotate: degrees(90) };
  if (rotation === 180) {
    return {
      ...size,
      x: crop.x + crop.width,
      y: crop.y + crop.height,
      rotate: degrees(180),
    };
  }
  if (rotation === 270)
    return {
      ...size,
      x: crop.x,
      y: crop.y + crop.height,
      rotate: degrees(270),
    };
  return { ...size, x: crop.x, y: crop.y };
}

export async function buildGradedPdf(originalBytes, annotations = []) {
  if (!originalBytes || originalBytes.byteLength === 0) {
    throw new Error(
      "Original PDF is not available. Reopen the script and try again.",
    );
  }

  const pdf = await PDFDocument.load(originalBytes, { ignoreEncryption: true });
  const pages = pdf.getPages();
  const byPage = new Map();

  annotations.forEach((ann) => {
    if (!ann?.page) return;
    if (!byPage.has(ann.page)) byPage.set(ann.page, []);
    byPage.get(ann.page).push(ann);
  });

  for (const [pageNo, pageAnnotations] of byPage) {
    const page = pages[pageNo - 1];
    if (!page) continue;

    const crop = page.getCropBox();
    const rotation = ((page.getRotation().angle % 360) + 360) % 360;
    const sideways = rotation === 90 || rotation === 270;
    const displayWidth = sideways ? crop.height : crop.width;
    const displayHeight = sideways ? crop.width : crop.height;

    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(displayWidth * EXPORT_SCALE);
    canvas.height = Math.ceil(displayHeight * EXPORT_SCALE);
    const ctx = canvas.getContext("2d");

    pageAnnotations.forEach((ann) =>
      drawAnnotation(ctx, ann, canvas.width, canvas.height, EXPORT_SCALE),
    );

    const image = await pdf.embedPng(await canvasToPngBytes(canvas));
    page.drawImage(
      image,
      overlayPlacement(crop, rotation, displayWidth, displayHeight),
    );
  }

  const bytes = await pdf.save();
  return new Blob([bytes], { type: "application/pdf" });
}
